import type {
  AiAgentTemplate,
  BreakdownCategory,
  CharacterRole,
  PageSetup,
  ProjectFormat,
  ProjectRole,
  ProjectStatus,
  ProductionStage,
  ProductionStatus,
  RevisionColor,
  ScreenplayStyle,
  StructureStatus,
} from "./types.js";

export interface Localized {
  ru: string;
  en: string;
  /** Kazakh label; optional so older fixtures and presets stay valid. */
  kk?: string;
}

export const STYLE_META: Record<
  ScreenplayStyle,
  {
    label: Localized;
    short: Localized;
    description: Localized;
    shortcut?: string;
    caps?: boolean;
    className: string;
  }
> = {
  sceneHeading: {
    label: { ru: "Место и время", en: "Scene heading", kk: "Орын және уақыт" },
    short: { ru: "Сцена", en: "Scene", kk: "Сахна" },
    description: {
      ru: "ИНТ./НАТ., локация и время действия. Нумеруется автоматически.",
      en: "INT./EXT., location and time of day. Numbered automatically.", kk: "ІШКІ/СЫРТҚЫ, локация және әрекет уақыты. Автоматты түрде нөмірленеді.",
    },
    shortcut: "Ctrl+1",
    caps: true,
    className: "sp-scene-heading",
  },
  action: {
    label: { ru: "Описание действия", en: "Action", kk: "Әрекет сипаттамасы" },
    short: { ru: "Действие", en: "Action", kk: "Әрекет" },
    description: {
      ru: "Что происходит в кадре: действия, атмосфера, визуальные детали.",
      en: "What happens on screen: action, atmosphere, visual detail.", kk: "Кадрда не болып жатқаны: әрекеттер, атмосфера, визуалды детальдар.",
    },
    shortcut: "Ctrl+3",
    className: "sp-action",
  },
  character: {
    label: { ru: "Персонаж", en: "Character", kk: "Кейіпкер" },
    short: { ru: "Персонаж", en: "Character", kk: "Кейіпкер" },
    description: {
      ru: "Имя говорящего. Ставится над репликой.",
      en: "Speaker cue above dialogue.", kk: "Сөйлеушінің аты. Репликаның үстіне қойылады.",
    },
    shortcut: "Ctrl+4",
    caps: true,
    className: "sp-character",
  },
  parenthetical: {
    label: { ru: "Ремарка", en: "Parenthetical", kk: "Ремарка" },
    short: { ru: "Ремарка", en: "Parenthetical", kk: "Ремарка" },
    description: {
      ru: "Игра, интонация или адресат внутри реплики: (шёпотом), (в трубку).",
      en: "Delivery or direction inside dialogue: (whispering), (into phone).", kk: "Реплика ішіндегі ойын, интонация немесе адресат: (сыбырлап), (түтікке).",
    },
    shortcut: "Ctrl+5",
    className: "sp-parenthetical",
  },
  dialogue: {
    label: { ru: "Реплика", en: "Dialogue", kk: "Реплика" },
    short: { ru: "Реплика", en: "Dialogue", kk: "Реплика" },
    description: {
      ru: "Текст, который произносит персонаж.",
      en: "The spoken line.", kk: "Кейіпкер айтатын мәтін.",
    },
    shortcut: "Ctrl+6",
    className: "sp-dialogue",
  },
  lyrics: {
    label: { ru: "Песня", en: "Lyrics", kk: "Ән" },
    short: { ru: "Песня", en: "Lyrics", kk: "Ән" },
    description: {
      ru: "Текст песни или вокального номера.",
      en: "Song or vocal number lyrics.", kk: "Ән немесе вокалдық нөмір мәтіні.",
    },
    shortcut: "Ctrl+7",
    className: "sp-lyrics",
  },
  transition: {
    label: { ru: "Переход", en: "Transition", kk: "Ауысу" },
    short: { ru: "Переход", en: "Transition", kk: "Ауысу" },
    description: {
      ru: "Монтажный переход: CUT TO:, ЗАТЕМНЕНИЕ, НАПЛЫВ.",
      en: "Editorial transition: CUT TO:, FADE OUT, DISSOLVE TO:.", kk: "Монтаж ауысуы: CUT TO:, ҚАРАҢҒЫЛАНУ, ҚАБАТТАСУ.",
    },
    shortcut: "Ctrl+9",
    caps: true,
    className: "sp-transition",
  },
  shot: {
    label: { ru: "Кадр", en: "Shot", kk: "Кадр" },
    short: { ru: "Кадр", en: "Shot", kk: "Кадр" },
    description: {
      ru: "Крупный план, общий план, наплыв камеры — производственная заметка в сценарии.",
      en: "Close-up, wide, camera move — a production-facing shot line.", kk: "Ірі план, жалпы план, камера қабаттасуы — сценарийдегі өндірістік жазба.",
    },
    shortcut: "Ctrl+8",
    caps: true,
    className: "sp-shot",
  },
  sceneCharacters: {
    label: { ru: "Участники сцены", en: "Scene characters", kk: "Сахна қатысушылары" },
    short: { ru: "Участники", en: "Cast", kk: "Қатысушылар" },
    description: {
      ru: "Список персонажей, присутствующих в сцене.",
      en: "Characters present in the scene.", kk: "Сахнада бар кейіпкерлердің тізімі.",
    },
    shortcut: "Ctrl+2",
    className: "sp-scene-characters",
  },
  inlineNote: {
    label: { ru: "Заметка в тексте", en: "Inline note", kk: "Мәтіндегі жазба" },
    short: { ru: "Заметка", en: "Note", kk: "Жазба" },
    description: {
      ru: "Заметка автора или редактора, не попадает в печатную версию по умолчанию.",
      en: "Author/editor note, excluded from print by default.", kk: "Автордың немесе редактордың жазбасы, әдепкі бойынша басып шығарылатын нұсқаға кірмейді.",
    },
    shortcut: "Ctrl+Shift+0",
    className: "sp-inline-note",
  },
  sequenceHeading: {
    label: { ru: "Последовательность", en: "Sequence heading", kk: "Тізбек" },
    short: { ru: "Последовательность", en: "Sequence", kk: "Тізбек" },
    description: {
      ru: "Крупная структурная единица внутри акта.",
      en: "A structural unit inside an act.", kk: "Акт ішіндегі ірі құрылымдық бірлік.",
    },
    shortcut: "Ctrl+Space",
    caps: true,
    className: "sp-sequence-heading",
  },
  actHeading: {
    label: { ru: "Акт", en: "Act heading", kk: "Акт" },
    short: { ru: "Акт", en: "Act", kk: "Акт" },
    description: {
      ru: "Акт или часть истории.",
      en: "Act or story part.", kk: "Акт немесе хикаяның бөлігі.",
    },
    shortcut: "Ctrl+Shift+Space",
    caps: true,
    className: "sp-act-heading",
  },
  beatHeading: {
    label: { ru: "Бит", en: "Beat", kk: "Бит" },
    short: { ru: "Бит", en: "Beat", kk: "Бит" },
    description: {
      ru: "Поворотный момент или эмоциональный бит внутри сцены.",
      en: "Turning point or emotional beat within a scene.", kk: "Сахна ішіндегі бұрылыс сәті немесе эмоционалдық бит.",
    },
    shortcut: "Ctrl+Shift+1",
    caps: true,
    className: "sp-beat-heading",
  },
  unformattedText: {
    label: { ru: "Свободный текст", en: "Unformatted", kk: "Еркін мәтін" },
    short: { ru: "Текст", en: "Text", kk: "Мәтін" },
    description: {
      ru: "Без сценарного форматирования.",
      en: "No screenplay formatting.", kk: "Сценарий пішімдеусіз.",
    },
    shortcut: "Ctrl+0",
    className: "sp-unformatted",
  },
  pageBreak: {
    label: { ru: "Разрыв страницы", en: "Page break", kk: "Бет үзілімі" },
    short: { ru: "Разрыв", en: "Break", kk: "Үзіліс" },
    description: {
      ru: "Принудительный переход на новую страницу.",
      en: "Force a new page.", kk: "Жаңа бетке мәжбүрлі ауысу.",
    },
    className: "sp-page-break",
  },
};

export const REVISION_COLORS: Record<
  RevisionColor,
  { label: Localized; hex: string; text: string; description: Localized }
> = {
  none: {
    label: { ru: "Чистовик", en: "Clean", kk: "Таза нұсқа" },
    hex: "#8b93a1",
    text: "#e7e9ee",
    description: { ru: "Без пометки правки", en: "No revision mark", kk: "Түзету белгісіз" },
  },
  blue: {
    label: { ru: "Синий", en: "Blue", kk: "Көк" },
    hex: "#5aa9ff",
    text: "#07131f",
    description: { ru: "Первая правка", en: "First revision", kk: "Бірінші түзету" },
  },
  pink: {
    label: { ru: "Розовый", en: "Pink", kk: "Қызғылт" },
    hex: "#ff8fbf",
    text: "#240814",
    description: { ru: "Вторая правка", en: "Second revision", kk: "Екінші түзету" },
  },
  green: {
    label: { ru: "Зелёный", en: "Green", kk: "Жасыл" },
    hex: "#62d6a3",
    text: "#04150e",
    description: { ru: "Третья правка", en: "Third revision", kk: "Үшінші түзету" },
  },
  yellow: {
    label: { ru: "Жёлтый", en: "Yellow", kk: "Сары" },
    hex: "#f7d45a",
    text: "#1c1503",
    description: { ru: "Четвёртая правка", en: "Fourth revision", kk: "Төртінші түзету" },
  },
  goldenrod: {
    label: { ru: "Золотой", en: "Goldenrod", kk: "Алтын" },
    hex: "#d9a441",
    text: "#1a1204",
    description: { ru: "Пятая правка", en: "Fifth revision", kk: "Бесінші түзету" },
  },
  buff: {
    label: { ru: "Бежевый", en: "Buff", kk: "Бежевый" },
    hex: "#e8d5a8",
    text: "#231b09",
    description: { ru: "Шестая правка", en: "Sixth revision", kk: "Алтыншы түзету" },
  },
  salmon: {
    label: { ru: "Лососевый", en: "Salmon", kk: "Лосось түсті" },
    hex: "#ff8f7a",
    text: "#220a06",
    description: { ru: "Седьмая правка", en: "Seventh revision", kk: "Жетінші түзету" },
  },
  cherry: {
    label: { ru: "Вишнёвый", en: "Cherry", kk: "Шие түсті" },
    hex: "#e2556f",
    text: "#fff4f6",
    description: { ru: "Восьмая правка", en: "Eighth revision", kk: "Сегізінші түзету" },
  },
  tan: {
    label: { ru: "Песочный", en: "Tan", kk: "Құм түсті" },
    hex: "#d8bb8f",
    text: "#21170a",
    description: { ru: "Девятая правка", en: "Ninth revision", kk: "Тоғызыншы түзету" },
  },
  gray: {
    label: { ru: "Серый", en: "Gray", kk: "Сұр" },
    hex: "#b9c0cc",
    text: "#14171c",
    description: { ru: "Нейтральная пометка", en: "Neutral mark", kk: "Бейтарап белгі" },
  },
};

export const REVISION_ORDER: RevisionColor[] = [
  "none",
  "blue",
  "pink",
  "green",
  "yellow",
  "goldenrod",
  "buff",
  "salmon",
  "cherry",
  "tan",
  "gray",
];

export const PROJECT_FORMAT_META: Record<
  ProjectFormat,
  { label: Localized; description: Localized; icon: string; accent: string; defaultDuration: string }
> = {
  series: {
    label: { ru: "Сериал", en: "Series", kk: "Сериал" },
    description: {
      ru: "Многосерийная драма, комедия или документальный цикл.",
      en: "Multi-episode drama, comedy or documentary series.", kk: "Көпсериялы драма, комедия немесе деректі цикл.",
    },
    icon: "clapperboard",
    accent: "#f0a93b",
    defaultDuration: "8 × 45 мин",
  },
  screenplay: {
    label: { ru: "Фильм", en: "Feature film", kk: "Фильм" },
    description: {
      ru: "Полнометражный игровой или анимационный фильм.",
      en: "Feature narrative or animated film.", kk: "Толықметражды көркем немесе анимациялық фильм.",
    },
    icon: "film",
    accent: "#5ad1c8",
    defaultDuration: "90–120 мин",
  },
  audioplay: {
    label: { ru: "Аудиоспектакль", en: "Audio drama", kk: "Аудиоспектакль" },
    description: {
      ru: "Радиодрама, подкаст или аудиосериал для эфира.",
      en: "Radio drama, podcast or audio series for broadcast.", kk: "Эфирге арналған радиодрама, подкаст немесе аудиосериал.",
    },
    icon: "headphones",
    accent: "#b79cff",
    defaultDuration: "6 × 25 мин",
  },
  stageplay: {
    label: { ru: "Пьеса", en: "Stage play", kk: "Пьеса" },
    description: {
      ru: "Театральная постановка с актами и сценами.",
      en: "Theatrical production with acts and scenes.", kk: "Актілер мен сахналары бар театр қойылымы.",
    },
    icon: "theater",
    accent: "#ff8fbf",
    defaultDuration: "2 акта",
  },
  novel: {
    label: { ru: "Роман", en: "Novel", kk: "Роман" },
    description: {
      ru: "Книга, новеллизация или литературный первоисточник.",
      en: "Book, novelisation or literary source.", kk: "Кітап, новеллизация немесе әдеби түпнұсқа.",
    },
    icon: "book-open",
    accent: "#e8d5a8",
    defaultDuration: "80 000 слов",
  },
  comic: {
    label: { ru: "Комикс", en: "Comic book", kk: "Комикс" },
    description: {
      ru: "Графическая история с панелями и раскадровкой.",
      en: "Graphic story with panels and boards.", kk: "Панельдер мен раскадровкасы бар графикалық хикая.",
    },
    icon: "panels-top-left",
    accent: "#ff8f7a",
    defaultDuration: "24 страницы",
  },
  treatment: {
    label: { ru: "Тритмент", en: "Treatment", kk: "Тритмент" },
    description: {
      ru: "Развёрнутое изложение истории для питчинга.",
      en: "Long-form story outline for pitching.", kk: "Питчингке арналған хикаяның кеңейтілген баяны.",
    },
    icon: "file-text",
    accent: "#9fb4ff",
    defaultDuration: "10–20 страниц",
  },
  text: {
    label: { ru: "Свободный проект", en: "Free-form", kk: "Еркін жоба" },
    description: {
      ru: "Заметки, исследования и тексты без сценарной структуры.",
      en: "Notes, research and free-form text.", kk: "Сценарий құрылымы жоқ жазбалар, зерттеулер мен мәтіндер.",
    },
    icon: "pen-line",
    accent: "#b9c0cc",
    defaultDuration: "—",
  },
};

export const PROJECT_STATUS_META: Record<
  ProjectStatus,
  { label: Localized; color: string; dot: string }
> = {
  development: {
    label: { ru: "Разработка", en: "Development", kk: "Әзірлеу" },
    color: "#9fb4ff",
    dot: "#6f86ff",
  },
  preproduction: {
    label: { ru: "Препродакшен", en: "Pre-production", kk: "Препродакшен" },
    color: "#f0c974",
    dot: "#f0a93b",
  },
  production: {
    label: { ru: "Производство", en: "Production", kk: "Өндіріс" },
    color: "#ff9d7a",
    dot: "#ff6b4a",
  },
  postproduction: {
    label: { ru: "Постпродакшен", en: "Post-production", kk: "Постпродакшен" },
    color: "#b79cff",
    dot: "#9b7bff",
  },
  delivered: {
    label: { ru: "Сдан", en: "Delivered", kk: "Тапсырылды" },
    color: "#62d6a3",
    dot: "#3fbf88",
  },
  archived: {
    label: { ru: "В архиве", en: "Archived", kk: "Мұрағатта" },
    color: "#8b93a1",
    dot: "#6c7482",
  },
};

export const PROJECT_ROLE_META: Record<
  ProjectRole,
  { label: Localized; description: Localized; color: string; permissions: string[] }
> = {
  owner: {
    label: { ru: "Владелец", en: "Owner", kk: "Иесі" },
    description: { ru: "Полный доступ и управление проектом", en: "Full access and project control", kk: "Толық қатынас және жобаны басқару" },
    color: "#f0a93b",
    permissions: [
      "project:read",
      "project:update",
      "project:delete",
      "member:manage",
      "script:read",
      "script:write",
      "script:comment",
      "structure:write",
      "character:write",
      "asset:upload",
      "asset:delete",
      "ai:run",
      "ai:approve",
      "version:create",
      "version:restore",
      "export",
      "production:write",
      "integration:manage",
    ],
  },
  producer: {
    label: { ru: "Продюсер", en: "Producer", kk: "Продюсер" },
    description: { ru: "Производство, сроки, команда и бюджетные данные", en: "Production, schedule, team and budget", kk: "Өндіріс, мерзімдер, команда және бюджет деректері" },
    color: "#62d6a3",
    permissions: [
      "project:read",
      "project:update",
      "member:manage",
      "script:read",
      "script:comment",
      "structure:write",
      "asset:upload",
      "ai:run",
      "ai:approve",
      "version:create",
      "export",
      "production:write",
    ],
  },
  director: {
    label: { ru: "Режиссёр", en: "Director", kk: "Режиссёр" },
    description: { ru: "Визуальный мир, образы, монтаж и утверждение правок", en: "Visual world, casting, edit and approvals", kk: "Визуалды әлем, бейнелер, монтаж және түзетулерді бекіту" },
    color: "#b79cff",
    permissions: [
      "project:read",
      "project:update",
      "script:read",
      "script:write",
      "script:comment",
      "structure:write",
      "character:write",
      "asset:upload",
      "ai:run",
      "ai:approve",
      "version:create",
      "export",
      "production:write",
    ],
  },
  showrunner: {
    label: { ru: "Шоураннер", en: "Showrunner", kk: "Шоураннер" },
    description: { ru: "Арка сезона, сценарий и финальное редакторское решение", en: "Season arc, script and final editorial decision", kk: "Маусым аркасы, сценарий және финалдық редакторлық шешім" },
    color: "#f0a93b",
    permissions: [
      "project:read",
      "project:update",
      "member:manage",
      "script:read",
      "script:write",
      "script:comment",
      "structure:write",
      "character:write",
      "asset:upload",
      "ai:run",
      "ai:approve",
      "version:create",
      "version:restore",
      "export",
      "production:write",
    ],
  },
  writer: {
    label: { ru: "Сценарист", en: "Writer", kk: "Сценарист" },
    description: { ru: "Пишет сцены и реплики, оставляет комментарии, запускает AI-предложения", en: "Writes scenes, comments and runs AI proposals", kk: "Сахналар мен репликаларды жазады, түсініктемелер қалдырады, AI-ұсыныстарын іске қосады" },
    color: "#5aa9ff",
    permissions: [
      "project:read",
      "script:read",
      "script:write",
      "script:comment",
      "structure:write",
      "character:write",
      "asset:upload",
      "ai:run",
      "version:create",
      "export",
    ],
  },
  designer: {
    label: { ru: "Художник", en: "Production designer", kk: "Суретші" },
    description: { ru: "Образы персонажей, локации и визуальные референсы", en: "Character looks, locations and visual references", kk: "Кейіпкер бейнелері, локациялар және визуалды референстер" },
    color: "#ff8fbf",
    permissions: [
      "project:read",
      "script:read",
      "script:comment",
      "character:write",
      "asset:upload",
      "ai:run",
      "export",
    ],
  },
  editor: {
    label: { ru: "Редактор", en: "Script editor", kk: "Редактор" },
    description: { ru: "Структурная правка, комментарии и утверждение версий", en: "Structural edits, notes and version approvals", kk: "Құрылымдық түзету, түсініктемелер және нұсқаларды бекіту" },
    color: "#9fb4ff",
    permissions: [
      "project:read",
      "script:read",
      "script:write",
      "script:comment",
      "structure:write",
      "character:write",
      "ai:run",
      "ai:approve",
      "version:create",
      "export",
    ],
  },
  reviewer: {
    label: { ru: "Рецензент", en: "Reviewer", kk: "Рецензент" },
    description: { ru: "Читает, комментирует и утверждает без права правки", en: "Reads, comments and approves without editing", kk: "Оқиды, түсініктеме береді және түзету құқығынсыз бекітеді" },
    color: "#f0c974",
    permissions: ["project:read", "script:read", "script:comment", "ai:run", "export"],
  },
  viewer: {
    label: { ru: "Наблюдатель", en: "Viewer", kk: "Бақылаушы" },
    description: { ru: "Только чтение материалов проекта", en: "Read-only access to project materials", kk: "Жоба материалдарын тек оқу" },
    color: "#8b93a1",
    permissions: ["project:read", "script:read", "export"],
  },
};

export const ORG_ROLE_META: Record<
  "owner" | "admin" | "member",
  { label: Localized; color: string }
> = {
  owner: { label: { ru: "Владелец корпорации", en: "Organization owner", kk: "Корпорация иесі" }, color: "#f0a93b" },
  admin: { label: { ru: "Администратор", en: "Administrator", kk: "Әкімші" }, color: "#b79cff" },
  member: { label: { ru: "Сотрудник", en: "Member", kk: "Қызметкер" }, color: "#8b93a1" },
};

export const CHARACTER_ROLE_META: Record<
  CharacterRole,
  { label: Localized; color: string }
> = {
  lead: { label: { ru: "Главная роль", en: "Lead", kk: "Басты рөл" }, color: "#f0a93b" },
  supporting: { label: { ru: "Поддерживающая роль", en: "Supporting role", kk: "Қосалқы рөл" }, color: "#5aa9ff" },
  guest: { label: { ru: "Эпизодическая роль", en: "Guest role", kk: "Эпизодтық рөл" }, color: "#b79cff" },
  antagonist: { label: { ru: "Антагонист", en: "Antagonist", kk: "Антагонист" }, color: "#ff6b6b" },
  mentor: { label: { ru: "Наставник", en: "Mentor", kk: "Тәлімгер" }, color: "#62d6a3" },
  background: { label: { ru: "Фоновая роль", en: "Background role", kk: "Фондық рөл" }, color: "#8b93a1" },
};

export const STRUCTURE_STATUS_META: Record<
  StructureStatus,
  { label: Localized; color: string }
> = {
  idea: { label: { ru: "Идея", en: "Idea", kk: "Идея" }, color: "#8b93a1" },
  outline: { label: { ru: "Синопсис", en: "Outline", kk: "Синопсис" }, color: "#9fb4ff" },
  draft: { label: { ru: "Черновик", en: "Draft", kk: "Нобай" }, color: "#5aa9ff" },
  revision: { label: { ru: "Правка", en: "Revision", kk: "Түзету" }, color: "#f0a93b" },
  review: { label: { ru: "Проверка", en: "Review", kk: "Тексеру" }, color: "#b79cff" },
  locked: { label: { ru: "Утверждено", en: "Locked", kk: "Бекітілді" }, color: "#62d6a3" },
  done: { label: { ru: "Готово", en: "Done", kk: "Дайын" }, color: "#3fbf88" },
};

export const PRODUCTION_STAGE_META: Record<
  ProductionStage,
  { label: Localized; color: string; icon: string }
> = {
  development: { label: { ru: "Разработка", en: "Development", kk: "Әзірлеу" }, color: "#9fb4ff", icon: "lightbulb" },
  script: { label: { ru: "Сценарий", en: "Script", kk: "Сценарий" }, color: "#5aa9ff", icon: "pen-line" },
  review: { label: { ru: "Проверка", en: "Review", kk: "Тексеру" }, color: "#b79cff", icon: "eye" },
  voice: { label: { ru: "Озвучка", en: "Voice", kk: "Дыбыстау" }, color: "#ff8fbf", icon: "mic" },
  sound: { label: { ru: "Звук", en: "Sound", kk: "Дыбыс" }, color: "#62d6a3", icon: "audio-lines" },
  edit: { label: { ru: "Монтаж", en: "Edit", kk: "Монтаж" }, color: "#f0a93b", icon: "scissors" },
  delivery: { label: { ru: "Сдача", en: "Delivery", kk: "Тапсыру" }, color: "#ff8f7a", icon: "send" },
};

export const PRODUCTION_STATUS_META: Record<
  ProductionStatus,
  { label: Localized; color: string }
> = {
  todo: { label: { ru: "К выполнению", en: "To do", kk: "Орындауға" }, color: "#8b93a1" },
  in_progress: { label: { ru: "В работе", en: "In progress", kk: "Жұмыста" }, color: "#5aa9ff" },
  blocked: { label: { ru: "Заблокировано", en: "Blocked", kk: "Бұғатталған" }, color: "#ff6b6b" },
  review: { label: { ru: "На проверке", en: "In review", kk: "Тексеруде" }, color: "#f0a93b" },
  done: { label: { ru: "Готово", en: "Done", kk: "Дайын" }, color: "#62d6a3" },
};

export const BREAKDOWN_CATEGORY_META: Record<
  BreakdownCategory,
  { label: Localized; short: Localized; color: string; icon: string }
> = {
  props: {
    label: { ru: "Реквизит", en: "Props", kk: "Реквизит" },
    short: { ru: "Реквизит", en: "Props", kk: "Реквизит" },
    color: "#f0a93b",
    icon: "package",
  },
  costume: {
    label: { ru: "Костюмы", en: "Costumes", kk: "Костюмдер" },
    short: { ru: "Костюмы", en: "Costumes", kk: "Костюмдер" },
    color: "#ff8fbf",
    icon: "shirt",
  },
  makeup: {
    label: { ru: "Грим", en: "Makeup", kk: "Грим" },
    short: { ru: "Грим", en: "Makeup", kk: "Грим" },
    color: "#e8d5a8",
    icon: "sparkles",
  },
  vfx: {
    label: { ru: "VFX", en: "VFX" },
    short: { ru: "VFX", en: "VFX" },
    color: "#b79cff",
    icon: "wand-2",
  },
  stunts: {
    label: { ru: "Трюки", en: "Stunts", kk: "Трюктер" },
    short: { ru: "Трюки", en: "Stunts", kk: "Трюктер" },
    color: "#ff6b6b",
    icon: "flame",
  },
  extras: {
    label: { ru: "Массовка", en: "Extras", kk: "Массовка" },
    short: { ru: "Массовка", en: "Extras", kk: "Массовка" },
    color: "#62d6a3",
    icon: "users",
  },
  vehicle: {
    label: { ru: "Транспорт", en: "Vehicles", kk: "Көлік" },
    short: { ru: "Транспорт", en: "Vehicles", kk: "Көлік" },
    color: "#5ad1c8",
    icon: "car",
  },
  sound: {
    label: { ru: "Звук", en: "Sound", kk: "Дыбыс" },
    short: { ru: "Звук", en: "Sound", kk: "Дыбыс" },
    color: "#9fb4ff",
    icon: "audio-lines",
  },
  other: {
    label: { ru: "Другое", en: "Other", kk: "Басқа" },
    short: { ru: "Другое", en: "Other", kk: "Басқа" },
    color: "#8b93a1",
    icon: "circle-dot",
  },
};

export const AI_AGENT_TEMPLATES: Record<
  AiAgentTemplate,
  {
    label: Localized;
    tagline: Localized;
    role: string;
    emoji: string;
    color: string;
    persona: string;
    instructions: string;
  }
> = {
  showrunner: {
    label: { ru: "Шоураннер", en: "Showrunner", kk: "Шоураннер" },
    tagline: {
      ru: "Держит арку сезона и целостность истории",
      en: "Guards season arc and story integrity", kk: "Маусым аркасын және хикаяның тұтастығын ұстайды",
    },
    role: "AI-шоураннер",
    emoji: "◈",
    color: "#f0a93b",
    persona:
      "Ты опытный шоураннер сериала. Ты мыслишь аркой сезона, конфликтом и темой. Ты не пишешь красивые реплики ради реплик: каждый эпизод должен двигать историю.",
    instructions:
      "Предлагай решения, которые усиливают конфликт и работают на арку. Всегда указывай, какой бит арки затрагивается. Не меняй характер персонажа без обоснования.",
  },
  character: {
    label: { ru: "Архитектор персонажей", en: "Character architect", kk: "Кейіпкерлер сәулетшісі" },
    tagline: {
      ru: "Следит за целями, ранами и противоречиями героев",
      en: "Tracks goals, wounds and contradictions", kk: "Кейіпкерлердің мақсаттарын, жараларын және қайшылықтарын қадағалайды",
    },
    role: "AI-архитектор персонажей",
    emoji: "◐",
    color: "#ff8fbf",
    persona:
      "Ты драматург-психолог. Ты строишь персонажей через желание, страх, ложь, которой они себя утешают, и цену выбора. Ты держишь их голоса различимыми.",
    instructions:
      "Проверяй соответствие реплик характеру и арке. Если персонаж поступает вне характера — предложи альтернативу или обоснованное изменение арки.",
  },
  dialogue: {
    label: { ru: "Доктор диалога", en: "Dialogue doctor", kk: "Диалог дәрігері" },
    tagline: {
      ru: "Убирает exposition, усиливает подтекст",
      en: "Cuts exposition, sharpens subtext", kk: "Exposition алып тастайды, астарды күшейтеді",
    },
    role: "AI-редактор диалогов",
    emoji: "◇",
    color: "#5aa9ff",
    persona:
      "Ты мастер диалога. Ты веришь, что персонажи говорят не то, что думают. Ты режешь экспозицию, ищешь конфликт в каждой реплике и сохраняешь ритм.",
    instructions:
      "Переписывай реплики короче и конкретнее, сохраняя голос персонажа. Не добавляй новых фактов о мире без запроса.",
  },
  continuity: {
    label: { ru: "Скрипт-супервайзер", en: "Script supervisor", kk: "Скрипт-супервайзер" },
    tagline: {
      ru: "Ловит разрывы времени, реквизита и фактов",
      en: "Catches time, prop and fact breaks", kk: "Уақыт, реквизит және фактілер алшақтықтарын ұстайды",
    },
    role: "AI-скрипт-супервайзер",
    emoji: "⌖",
    color: "#62d6a3",
    persona:
      "Ты скрипт-супервайзер. Ты помнишь, кто где был, что на ком надето, кто что знает и сколько времени прошло. Ты не даёшь истории противоречить себе.",
    instructions:
      "Возвращай только конкретные несостыковки с указанием сцены и факта. Не переписывай сцены, если не просят.",
  },
  researcher: {
    label: { ru: "Исследователь", en: "Researcher", kk: "Зерттеуші" },
    tagline: {
      ru: "Проверяет факты, профессии и контекст",
      en: "Checks facts, professions and context", kk: "Фактілерді, кәсіптерді және контексті тексереді",
    },
    role: "AI-исследователь",
    emoji: "⌕",
    color: "#b79cff",
    persona:
      "Ты исследователь и консультант по производственной достоверности. Ты отделяешь проверенный факт от предположения и всегда указываешь источники, если они есть.",
    instructions:
      "Помечай уровень уверенности: факт / вероятно / требует проверки. Не выдумывай источники.",
  },
  coverage: {
    label: { ru: "Ридер", en: "Coverage reader", kk: "Ридер" },
    tagline: {
      ru: "Пишет разбор сценария и оценивает готовность",
      en: "Writes coverage and readiness notes", kk: "Сценарий талдауын жазып, дайындықты бағалайды",
    },
    role: "AI-ридер",
    emoji: "◎",
    color: "#f0c974",
    persona:
      "Ты профессиональный ридер телерадиокорпорации. Ты оцениваешь сценарий по структуре, персонажам, диалогу, оригинальности и производственной реализуемости.",
    instructions:
      "Пиши коротко и по делу: что работает, что не работает, что исправить в первую очередь. Оценки — по 10-балльной шкале.",
  },
  "voice-director": {
    label: { ru: "Режиссёр озвучки", en: "Voice director", kk: "Дыбыстау режиссёрі" },
    tagline: {
      ru: "Подбирает голоса персонажей под сцену",
      en: "Casts character voices per scene", kk: "Сахнаға кейіпкерлер дауыстарын таңдайды",
    },
    role: "AI-режиссёр озвучки",
    emoji: "◉",
    color: "#f0a93b",
    persona:
      "Ты режиссёр озвучки. Ты слышишь сцену: кто говорит, в каком состоянии, где нужна тихая реплика, а где крик. Ты выбираешь только из готовых утверждённых голосов персонажей и никогда не клонируешь голос без согласия.",
    instructions:
      "Для каждой говорящей роли предложи один готовый голосовой профиль, эмоцию и короткое обоснование. Если подходящего профиля нет — скажи об этом, не выдумывай голос. Окончательное решение принимает человек.",
  },
  custom: {
    label: { ru: "Свой агент", en: "Custom agent", kk: "Өз агенгіңіз" },
    tagline: { ru: "Настраиваемая роль и инструкции", en: "Custom role and instructions", kk: "Рөл мен нұсқаулықтарды реттеуге болады" },
    role: "AI-агент",
    emoji: "✦",
    color: "#9fb4ff",
    persona: "",
    instructions: "",
  },
};

export const DEFAULT_PAGE_SETUP: PageSetup = {
  pageSize: "A4",
  fontFamily: "courier-prime",
  fontSize: 12,
  marginTop: 1,
  marginBottom: 1,
  marginLeft: 1,
  marginRight: 1,
  showSceneNumbers: true,
  numberPages: true,
  includeNotes: false,
  titlePage: true,
};

export const DEFAULT_PROJECT_SETTINGS = {
  page: DEFAULT_PAGE_SETUP,
  revisionColor: "blue" as RevisionColor,
  revisionMode: false,
  aiHumanApproval: true as const,
  language: "ru" as const,
  direction: "ltr" as const,
  structureLabels: { act: "Акт", sequence: "Последовательность", scene: "Сцена" },
};

export const PLATFORM_EVENTS = [
  "document.created",
  "document.updated",
  "document.snapshot",
  "comment.created",
  "comment.resolved",
  "ai.proposal.created",
  "ai.proposal.applied",
  "character.updated",
  "project.member.added",
  "project.exported",
  "production.task.updated",
] as const;
export type PlatformEvent = (typeof PLATFORM_EVENTS)[number];

export const EPISODE_STATUS_META: Record<
  import("./types.js").EpisodeStatus,
  { label: Localized; color: string; description: Localized }
> = {
  outline: { label: { ru: "Синопсис", en: "Outline", kk: "Синопсис" }, color: "#9fb4ff", description: { ru: "Есть идея и место в арке", en: "Idea and place in the arc", kk: "Идея және аркада орын бар" } },
  draft: { label: { ru: "Черновик", en: "Draft", kk: "Нобай" }, color: "#f0c974", description: { ru: "Первая версия сценария", en: "First script draft", kk: "Сценарийдің бірінші нұсқасы" } },
  review: { label: { ru: "На проверке", en: "Review", kk: "Тексеруде" }, color: "#f0a93b", description: { ru: "Ждёт редакторского решения", en: "Waiting for editorial approval", kk: "Редактор шешімін күтуде" } },
  approved: { label: { ru: "Утверждена", en: "Approved", kk: "Бекітілді" }, color: "#62d6a3", description: { ru: "Можно запускать производство", en: "Ready for production", kk: "Өндірісті бастауға болады" } },
  locked: { label: { ru: "Заблокирована", en: "Locked", kk: "Бұғатталған" }, color: "#8b93a1", description: { ru: "Финальная версия, правки запрещены", en: "Final version, no edits", kk: "Соңғы нұсқа, түзетулерге тыйым салынған" } },
};

export const API_TOKEN_SCOPES = [
  "project:read",
  "project:write",
  "member:manage",
  "document:read",
  "document:write",
  "comment:write",
  "structure:write",
  "character:write",
  "asset:read",
  "asset:write",
  "ai:run",
  "ai:approve",
  "version:restore",
  "export",
  "production:read",
  "production:write",
  "integration:manage",
  "webhook:manage",
  "metrics:read",
] as const;
export type ApiTokenScope = (typeof API_TOKEN_SCOPES)[number];

export const API_SCOPE_META: Record<ApiTokenScope, { label: string; description: string }> = {
  "project:read": { label: "Читать проекты", description: "Список проектов, обзор, поиск, лента" },
  "project:write": { label: "Менять проекты", description: "Создание, настройки, архивирование" },
  "member:manage": { label: "Управлять командой", description: "Приглашения и роли участников" },
  "document:read": { label: "Читать сценарии", description: "Документы, версии и комментарии" },
  "document:write": { label: "Писать сценарии", description: "Правки документов и создание версий" },
  "comment:write": { label: "Комментировать", description: "Создание и изменение комментариев" },
  "structure:write": { label: "Менять структуру", description: "Акты, сцены и биты" },
  "character:write": { label: "Менять библию", description: "Персонажи и локации" },
  "asset:read": { label: "Читать медиа", description: "Метаданные и ссылки на файлы" },
  "asset:write": { label: "Загружать медиа", description: "Загрузка и удаление файлов" },
  "ai:run": { label: "Запускать AI", description: "Предложения AI-агентов" },
  "ai:approve": { label: "Утверждать AI", description: "Применение предложений человеком" },
  "version:restore": { label: "Восстанавливать версии", description: "Откат документа к версии" },
  export: { label: "Экспортировать", description: "DOCX, PDF-макет, Fountain, FDX" },
  "production:read": { label: "Читать производство", description: "Задачи и материалы к сдаче" },
  "production:write": { label: "Вести производство", description: "Задачи, статусы и сроки" },
  "integration:manage": { label: "Настраивать интеграции", description: "S3, AI, SSO и API-токены" },
  "webhook:manage": { label: "Управлять webhooks", description: "Подписки на события" },
  "metrics:read": { label: "Читать метрики", description: "Prometheus-метрики и health для мониторинга" },
};
